import { ReactNode, useMemo } from 'react';

import LinearProgress from '@mui/material/LinearProgress';
import Skeleton from '@mui/material/Skeleton';
import { styled } from '@mui/material/styles';
import {
  gridColumnPositionsSelector,
  gridColumnsTotalWidthSelector,
  gridDensityRowHeightSelector,
  useGridApiContext,
} from '@mui/x-data-grid';

const SkeletonCell = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
});

export function DataGridLoadingOverlay(): JSX.Element {
  const { current: gridApi } = useGridApiContext();

  const viewportHeight = gridApi.getRootDimensions()?.viewportInnerSize.height ?? 0;

  const rowHeight = gridDensityRowHeightSelector(gridApi.state, gridApi.instanceId);
  const skeletonRowsCount = Math.ceil(viewportHeight / rowHeight) || 2;

  const totalWidth = gridColumnsTotalWidthSelector(gridApi.state, gridApi.instanceId);
  const positions = gridColumnPositionsSelector(gridApi.state, gridApi.instanceId);
  const inViewportCount = useMemo(() => positions.filter((value) => value <= totalWidth).length, [totalWidth, positions]);
  const columns = gridApi.getVisibleColumns().slice(0, inViewportCount);

  const children = useMemo(() => {
    // reseed random number generator to create stable lines betwen renders
    const array: ReactNode[] = [];

    for (let i = 0; i < skeletonRowsCount; i += 1) {
      for (const column of columns) {
        array.push(
          <SkeletonCell key={`col-${column.field}-${i}`} sx={{ justifyContent: column.align }}>
            <Skeleton sx={{ mx: 1 }} width={`32%`} />
          </SkeletonCell>,
        );
      }
      array.push(<SkeletonCell key={`fill-${i}`} />);
    }
    return array;
  }, [skeletonRowsCount, columns]);

  const rowsCount = gridApi.getRowsCount();

  return rowsCount > 0 ? (
    <LinearProgress />
  ) : (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: `${columns.map(({ computedWidth }) => `${computedWidth}px`).join(' ')} 1fr`,
        gridAutoRows: `${rowHeight}px`,
      }}
    >
      {children}
    </div>
  );
}

export default DataGridLoadingOverlay;
